import { useState, useEffect } from 'react';
import skillDevelopmentService from '../services/skillDevelopmentService';

export const useSkillDevelopment = () => {
  const [skillData, setSkillData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSkillData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const rawData = await skillDevelopmentService.getAllCourses();
      const transformedData = skillDevelopmentService.transformCoursesData(rawData);
      setSkillData(transformedData);
    } catch (err) {
      // Don't use fallback data - show error message instead
      console.error('Error in useSkillDevelopment:', err);
      
      setSkillData(null);
      setError('Failed to load skill development statistics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSkillData();
  }, []);

  const refetch = () => {
    fetchSkillData();
  };

  return {
    skillData,
    loading,
    error,
    refetch,
  };
};

// Hook for training categories
export const useTrainingCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const categoriesData = await skillDevelopmentService.getTrainingCategories();
      if (categoriesData && categoriesData.length > 0) {
        setCategories(categoriesData);
      } else {
        console.warn('No categories received, using empty array');
        setCategories([]);
      }
    } catch (err) {
      console.warn('Error fetching training categories:', err);
      setError('Failed to load categories');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const refetch = () => {
    fetchCategories();
  };

  return {
    categories,
    loading,
    error,
    refetch,
  };
};

// Hook for categorywise courses
export const useCategorywiseCourses = (categoryId) => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCourses = async (catId) => {
    if (!catId) {
      setCourses([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const coursesData = await skillDevelopmentService.getCategorywiseCourses(catId);
      setCourses(coursesData);
    } catch (err) {
      console.warn('Error fetching categorywise courses:', err);
      setError('Failed to load courses');
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses(categoryId);
  }, [categoryId]);

  const refetch = () => {
    fetchCourses(categoryId);
  };

  return {
    courses,
    loading,
    error,
    refetch,
  };
};

// Hook for upcoming courses
export const useUpcomingCourses = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchUpcomingCourses = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const coursesData = await skillDevelopmentService.getUpcomingCourses();
      setCourses(coursesData);
    } catch (err) {
      console.warn('Error fetching upcoming courses:', err);
      setError('Failed to load upcoming courses');
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUpcomingCourses();
  }, []);

  const refetch = () => {
    fetchUpcomingCourses();
  };

  return {
    courses,
    loading,
    error,
    refetch,
  };
};

// Hook for course details
export const useCourseDetails = (courseId) => {
  const [courseDetails, setCourseDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCourseDetails = async (id) => {
    if (!id) {
      setCourseDetails(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const detailsData = await skillDevelopmentService.getCourseDetails(id);
      setCourseDetails(detailsData);
    } catch (err) {
      console.warn('Error fetching course details:', err);
      setError('Failed to load course details');
      setCourseDetails(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourseDetails(courseId);
  }, [courseId]);

  const refetch = () => {
    fetchCourseDetails(courseId);
  };

  return {
    courseDetails,
    loading,
    error,
    refetch,
  };
};

export default useSkillDevelopment;
